import Component from "@glimmer/component";
import { on } from "@ember/modifier";
import type { Step } from "#models/step.ts";

import "./step-pad.css";

export interface StepPadSignature {
  Args: {
    step: Step;
    isCurrentStep?: boolean;
    onClick?: () => void;
  };
  Element: HTMLButtonElement;
}

export default class StepPadComponent extends Component<StepPadSignature> {
  get velocity(): number {
    return this.args.step?.velocity ?? 0;
  }

  get isCurrent(): boolean {
    return Boolean(this.args.isCurrentStep);
  }

  handleClick = () => {
    if (this.args.onClick) {
      this.args.onClick();
    }
  };

  /**
   * Pad body styling.
   * Defaults to basic gray background for fallback/standalone, overridden by container rules like
   * bg-beat-group-1, etc.
   * Slightly muted when inactive, brighter with glowing border when active.
   */
  get padClasses(): string {
    const base = `
      step-pad
      relative flex flex-col items-center justify-between
      w-10 h-10 rounded-lg 
      transition-all duration-150 select-none
      focus:outline-none focus:ring-1 focus:ring-white cursor-pointer border border-black/40
      bg-gray-500
    `;

    if (this.isCurrent) {
      // Brighter pad body when active / on current beat
      return `${base} opacity-100 ring-1 ring-white/90 shadow-around`;
    }

    // Inactive - muted beat group color
    return `${base} opacity-75 hover:opacity-90`;
  }

  /**
   * LED class bindings based on velocity:
   * - 0.0: Unlit / dark
   * - 0.5: Yellow
   * - 1.0: Red
   * Bright glow applied when step is active.
   */
  get ledClasses(): string {
    const base = "w-3 h-3 rounded-full transition-all duration-150 border";

    if (this.velocity === 0) {
      // Unlit LED (practically invisible)
      return `${base} bg-transparent border-neutral-100 shadow-inner opacity-10`;
    }

    if (this.velocity === 0.5) {
      if (this.isCurrent) {
        // Yellow LED with intense glow when active
        return `${base} bg-yellow-200 border-yellow-700 shadow-glow-yellow`;
      }
      // Standard Yellow LED
      return `${base} bg-yellow-400 border-yellow-700`;
    }

    // Velocity === 1.0 (Red LED)
    if (this.isCurrent) {
      // Red LED with intense glow when active
      return `${base} bg-red-200 border-red-700 shadow-glow-red`;
    }
    // Standard Red LED
    return `${base} bg-red-600 border-red-700`;
  }

  get ledColor(): "off" | "yellow" | "red" {
    if (this.velocity === 0.5) return "yellow";
    if (this.velocity === 1.0) return "red";
    return "off";
  }

  <template>
    <button
      type="button"
      class={{this.padClasses}}
      data-test-step-pad
      data-test-step-velocity={{this.velocity}}
      data-test-step-active={{if this.isCurrent "true" "false"}}
      {{on "click" this.handleClick}}
      ...attributes
    >
      {{! Top accent }}
      <div class="w-full h-1/3 bg-white/30 rounded-t-lg"></div>

      {{! LED Indicator }}
      <div class="pb-2">
        <div
          class={{this.ledClasses}}
          data-test-step-led
          data-test-led-color={{this.ledColor}}
          data-test-led-glowing={{if this.isCurrent "true" "false"}}
        ></div>
      </div>
    </button>
  </template>
}
